/**
 * The chip ladder. Copied verbatim from `ChipPlacementPanel.tsx:78-87` (the
 * const is module-private there and the shared panel is never modified), and
 * lifted out of `BirdieBoard.tsx` in B20 Task 2 so the TILE can paint a placed
 * stake in the same colour the bar chip the player picked was: grey 0.7, teal 1,
 * blue 5, red 20, gold 100, green 500, purple 2k.
 *
 * Pure and dependency-free, which is the point — the pile a tile draws and the
 * disc the bar draws now agree by construction rather than by two hard-coded
 * gradients that drifted (the tile's was a single amber-brown, whatever the
 * player had bet with).
 */

export interface ChipStyle {
  bg: string
  border: string
  color: string
}

/** Ladder rung 0. Also the fallback for an amount below every rung. */
export const CHIP_STYLE_GREY: ChipStyle = {
  bg: 'linear-gradient(160deg, #3d3d3d, #1a1a1a)',
  border: '#666666',
  color: '#bbb'
}

export const CHIP_STYLES: readonly ChipStyle[] = [
  CHIP_STYLE_GREY,
  { bg: 'linear-gradient(160deg, #1a5c4c, #0a3325)', border: '#28b890', color: '#5eeac4' },
  { bg: 'linear-gradient(160deg, #2a4a8a, #142850)', border: '#4488dd', color: '#99ccff' },
  { bg: 'linear-gradient(160deg, #b83030, #6b1a1a)', border: '#e05050', color: '#ffbbbb' },
  { bg: 'linear-gradient(160deg, #c47b10, #7a4d08)', border: '#e8a020', color: '#ffe0a0' },
  { bg: 'linear-gradient(160deg, #1f8a1f, #0d4a0d)', border: '#30b030', color: '#90ee90' },
  { bg: 'linear-gradient(160deg, #1a1a1a, #000000)', border: '#9060d0', color: '#c8a8f0' }
]

/** `noUncheckedIndexedAccess`: index into the palette without a `!`. */
export function chipStyleAt(index: number): ChipStyle {
  return CHIP_STYLES[index % CHIP_STYLES.length] ?? CHIP_STYLE_GREY
}

/** A bar chip: its face value and the ladder colours painted on it. */
export interface ChipRung {
  value: number
  bg: string
  border: string
  color: string
}

/**
 * The colour a placed TOTAL is drawn in — the highest rung the amount reaches,
 * exactly the shared panel's rule (`getChipStyleForAmount`,
 * `ChipPlacementPanel.tsx:163-177`). The rung list is the LIVE bar, not a
 * module constant, because the chip values are config- and balance-driven and
 * change between operators and currencies.
 *
 * Four chips of 5 is a 20 stake, which is a red pile, not four blue ones: the
 * pile says how much is on the option, and the ladder is how the player reads
 * "how much" at a glance. An empty ladder falls back to grey rather than
 * throwing — a board can render one frame before the chip values resolve.
 */
export function chipStyleForAmount(amount: number, rungs: readonly ChipRung[]): ChipStyle {
  const first = rungs[0]
  const fallback: ChipStyle = first
    ? { bg: first.bg, border: first.border, color: first.color }
    : CHIP_STYLE_GREY
  for (let i = rungs.length - 1; i >= 0; i--) {
    const rung = rungs[i]
    if (rung && amount >= rung.value) {
      return { bg: rung.bg, border: rung.border, color: rung.color }
    }
  }
  return fallback
}

/**
 * How many SHADOW chips sit behind the front one. The Footfall pile
 * (`chip-placement.scss` ~1640): one layer per extra placement, three at most,
 * so a tile a player has tapped ten times reads as "a pile" and not as a tower
 * that leaves its own tile.
 */
export const CHIP_PILE_MAX_LAYERS = 3

export function chipPileLayers(count: number): number {
  if (!Number.isFinite(count)) return 0
  return Math.max(0, Math.min(CHIP_PILE_MAX_LAYERS, Math.floor(count) - 1))
}
